import { Cause, Exit, Option, Predicate, Record } from 'effect';

type TaggedError<E> = Extract<E, { readonly _tag: string }>;

export type ThrowOnTag<E> = {
  readonly [Tag in TaggedError<E>['_tag']]?: (
    error: Extract<E, { readonly _tag: Tag }>,
  ) => unknown;
};

/**
 * Resolves a loader-side exit: the success value, or a throw. A tagged failure
 * listed in `throwOnTag` is replaced by what its handler returns, because
 * TanStack Router only recognises `notFound()` and `redirect()` when a loader
 * throws them; any other failure throws the squashed cause.
 */
export const valueOrThrow = <A, E>(
  exit: Exit.Exit<A, E>,
  throwOnTag?: ThrowOnTag<E>,
) => {
  if (Exit.isSuccess(exit)) return exit.value;
  const handlers: Record.ReadonlyRecord<
    string,
    ((error: never) => unknown) | undefined
  > = { ...throwOnTag };
  const handled = Cause.findErrorOption(exit.cause).pipe(
    Option.flatMap((error) =>
      Record.findFirst(
        handlers,
        (handler, tag) =>
          handler !== undefined && Predicate.isTagged(error, tag),
      ).pipe(
        // The matched tag makes `error` that handler's own error type.
        Option.map(([, handler]) => handler?.(error as never)),
      ),
    ),
  );
  throw Option.getOrElse(handled, () => Cause.squash(exit.cause));
};
